import { Component, Input, Output, EventEmitter, ViewChild, ChangeDetectorRef } from "@angular/core";
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { TypeManagerDecorator } from "../../../../main/type.map.service";
import { S25Util } from "../../../../util/s25-util";
import { Budget } from "../Budget";
import { BudgetPlanService } from "./budget.plan.service";
import { S25ButtonComponent } from "../../../../standalone/s25.button.component";
import { S25ModalModule } from "../../../s25-modal/s25.modal.module";
import { S25DropdownPaginatedModule } from "../../../s25-dropdown/s25.dropdown.paginated.module";

@TypeManagerDecorator("s25-ng-budget-plan-modal")
@Component({
    selector: "s25-ng-budget-plan-modal",
    template: `
        <s25-ng-modal #budgetPlanModal [title]="modalTitle" [type]="'custom'" [size]="'lg'">
            <ng-template #s25ModalBody>
                @if (showBudgetPlanForm) {
                    <form [formGroup]="budgetPlanForm" (ngSubmit)="onSubmit()" class="budget-form">
                        <!-- Basic Information -->
                        <div class="form-section">
                            <h4>Basic Information</h4>

                            <div class="form-row">
                                <div class="form-field">
                                    <label for="planName" class="required">Plan Name</label>
                                    <input
                                        type="text"
                                        id="planName"
                                        class="c-input"
                                        formControlName="name"
                                        placeholder="Enter plan name"
                                        maxlength="100"
                                    />

                                    @if (budgetPlanForm.get("name")?.invalid && budgetPlanForm.get("name")?.touched) {
                                        <div class="validation-error">
                                            @if (budgetPlanForm.get("name")?.errors?.["required"]) {
                                                Plan name is required
                                            }
                                            @if (budgetPlanForm.get("name")?.errors?.["minlength"]) {
                                                Plan name must be at least 3 characters
                                            }
                                        </div>
                                    }
                                </div>
                            </div>

                            <div class="form-row">
                                <div class="form-field full-width">
                                    <label for="budget" class="required">Budget</label>
                                    <s25-ng-dropdown-search-criteria
                                        type="budgets"
                                        formControlName="budget"
                                        [customFilterValue]="orgId"
                                        placeholder="Select a Budget"
                                        (chosenChange)="onBudgetSelect($event)"
                                    ></s25-ng-dropdown-search-criteria>
                                </div>
                            </div>

                            <div class="form-row">
                                <div class="form-field">
                                    <label for="planDescription">Description</label>
                                    <textarea
                                        id="planDescription"
                                        class="c-input"
                                        formControlName="description"
                                        placeholder="Enter plan description (optional)"
                                        rows="3"
                                        maxlength="500"
                                    ></textarea>
                                </div>
                            </div>

                            <div class="form-row">
                                <div class="form-field">
                                    <label for="planType" class="required">Plan Type</label>
                                    <s25-ng-dropdown-search-criteria
                                        type="budgetPlanTypes"
                                        formControlName="planType"
                                        placeholder="Select plan type"
                                    ></s25-ng-dropdown-search-criteria>
                                    @if (
                                        budgetPlanForm.get("planType")?.invalid &&
                                        budgetPlanForm.get("planType")?.touched
                                    ) {
                                        <div class="validation-error">Plan type is required</div>
                                    }
                                </div>
                            </div>
                        </div>

                        <!-- Budget Allocation -->
                        <div class="form-section">
                            <h4>Budget Allocation</h4>

                            <div class="form-row">
                                <div class="form-field">
                                    <label for="allocatedAmount" class="required">Allocated Amount</label>
                                    <input
                                        type="number"
                                        id="allocatedAmount"
                                        class="c-input"
                                        formControlName="allocatedAmount"
                                        placeholder="0.00"
                                        min="0"
                                        step="0.01"
                                    />
                                    @if (
                                        budgetPlanForm.get("allocatedAmount")?.invalid &&
                                        budgetPlanForm.get("allocatedAmount")?.touched
                                    ) {
                                        <div class="validation-error">
                                            @if (budgetPlanForm.get("allocatedAmount")?.errors?.["required"]) {
                                                Allocated amount is required
                                            }
                                            @if (budgetPlanForm.get("allocatedAmount")?.errors?.["min"]) {
                                                Amount must be greater than 0
                                            }
                                        </div>
                                    }
                                </div>
                            </div>

                            <div class="form-row">
                                <div class="form-field">
                                    <label for="startDate" class="required">Start Date</label>
                                    <input
                                        type="date"
                                        id="startDate"
                                        class="c-input"
                                        formControlName="startDate"
                                        (change)="onStartDateChange($event.target.value)"
                                    />
                                    @if (
                                        budgetPlanForm.get("startDate")?.invalid &&
                                        budgetPlanForm.get("startDate")?.touched
                                    ) {
                                        <div class="validation-error">Start date is required</div>
                                    }
                                </div>

                                <div class="form-field">
                                    <label for="endDate" class="required">End Date</label>
                                    <input
                                        type="date"
                                        id="endDate"
                                        class="c-input"
                                        formControlName="endDate"
                                        [min]="minEndDate ? minEndDate.toISOString().split('T')[0] : ''"
                                    />
                                    @if (
                                        budgetPlanForm.get("endDate")?.invalid && budgetPlanForm.get("endDate")?.touched
                                    ) {
                                        <div class="validation-error">End date is required</div>
                                    }
                                </div>
                            </div>

                            @if (budgetPlanForm.errors?.["dateRange"]) {
                                <div class="validation-error">End date must be after start date</div>
                            }
                        </div>

                        <!-- Additional Information -->
                        <div class="form-section">
                            <h4>Additional Information</h4>

                            <div class="form-row">
                                <div class="form-field">
                                    <label for="comments">Comments</label>
                                    <textarea
                                        id="comments"
                                        class="c-input"
                                        formControlName="comments"
                                        placeholder="Additional comments or notes (optional)"
                                        rows="3"
                                        maxlength="1000"
                                    ></textarea>
                                </div>
                            </div>

                            <div class="info-box">
                                <strong>Note:</strong> Budget plans are saved as drafts. After adding line items, use
                                the "Submit for Approval" button to submit for workflow approval.
                            </div>
                        </div>

                        <!-- Form Actions -->
                        <div class="form-actions">
                            <s25-ng-button
                                [type]="'outline'"
                                [buttonText]="'Cancel'"
                                [onClick]="cancelForm"
                            ></s25-ng-button>
                            <s25-ng-button
                                [type]="'primary'"
                                [buttonText]="getSubmitButtonText()"
                                [onClick]="onSubmit"
                                [isLoading]="isSubmitting"
                                [loadingText]="'Saving...'"
                                [disabled]="budgetPlanForm.invalid"
                            ></s25-ng-button>
                        </div>
                    </form>
                }
            </ng-template>
        </s25-ng-modal>
    `,
    styles: `
        .budget-form {
            display: flex;
            flex-direction: column;
            gap: 1.5rem;
        }

        .form-section {
            border: 1px solid #e5e5e5;
            border-radius: 8px;
            padding: 1.5rem;
            background: #fafafa;
        }

        .form-section h4 {
            margin: 0 0 1rem 0;
            color: #333;
            font-size: 1.1rem;
            font-weight: 600;
        }

        .form-row {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 1.5rem;
            margin-bottom: 1rem;
        }

        .form-row:last-child {
            margin-bottom: 0;
        }

        .form-field {
            display: flex;
            flex-direction: column;
        }

        .form-field.full-width {
            grid-column: 1 / -1;
        }

        .form-field label {
            font-weight: 600;
            color: #333;
            margin-bottom: 0.5rem;
            font-size: 0.9rem;
        }

        .form-field label.required::after {
            content: " *";
            color: #d32f2f;
        }

        .form-field select {
            border: 1px solid #ddd;
            border-radius: 4px;
            font-size: 0.9rem;
            background: white;
        }

        .form-field select:focus {
            outline: none;
            border-color: #2196f3;
            box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.2);
        }

        .validation-error {
            color: #d32f2f;
            font-size: 0.8rem;
            margin-top: 0.25rem;
        }

        .help-text {
            font-size: 0.85rem;
            color: #666;
            margin-top: 0.5rem;
            line-height: 1.5;
        }

        .info-box {
            background: #e3f2fd;
            border-left: 4px solid #2196f3;
            padding: 1rem;
            border-radius: 4px;
            font-size: 0.9rem;
            color: #1565c0;
            margin-top: 1rem;
        }

        .info-box strong {
            color: #0d47a1;
        }

        .form-actions {
            display: flex;
            gap: 1rem;
            justify-content: flex-end;
            padding-top: 1rem;
            border-top: 1px solid #e5e5e5;
        }

        @media (max-width: 768px) {
            .form-row {
                grid-template-columns: 1fr;
                gap: 1rem;
            }

            .form-actions {
                flex-direction: column;
                align-items: stretch;
            }

            .form-actions s25-ng-button {
                width: 100%;
            }
        }
    `,
    standalone: true,
    imports: [S25ButtonComponent, S25ModalModule, FormsModule, ReactiveFormsModule, S25DropdownPaginatedModule],
})
export class BudgetPlanModalComponent {
    @Input() orgId: number;
    @Input() planId?: number; // For edit mode

    @Output() planSaved = new EventEmitter<Budget.BudgetPlan>();
    @Output() planCancelled = new EventEmitter<void>();

    @ViewChild("budgetPlanModal") budgetPlanModal: any;

    budgetPlanForm: FormGroup;
    minEndDate: Date | null = null;

    showBudgetPlanForm = false;
    isEditMode = false;
    editingBudgetPlan: Budget.BudgetPlan | null = null;
    isSubmitting = false;

    get modalTitle(): string {
        return this.isEditMode ? "Edit Budget Plan" : "Create Budget Plan";
    }

    constructor(
        private fb: FormBuilder,
        private cd: ChangeDetectorRef,
    ) {
        this.budgetPlanForm = this.createForm();
    }

    private createForm(): FormGroup {
        return this.fb.group(
            {
                name: ["", [Validators.required, Validators.minLength(3)]],
                budget: [null, Validators.required],
                description: [""],
                planType: ["", Validators.required],
                allocatedAmount: [0, [Validators.required, Validators.min(0)]],
                startDate: [null, Validators.required],
                endDate: [null, Validators.required],
                comments: [""],
            },
            { validators: this.dateRangeValidator },
        );
    }

    private dateRangeValidator(group: FormGroup) {
        const startDate = group.get("startDate")?.value;
        const endDate = group.get("endDate")?.value;

        if (startDate && endDate && startDate >= endDate) {
            return { dateRange: true };
        }

        return null;
    }

    openCreateModal(): void {
        this.isEditMode = false;
        this.editingBudgetPlan = null;
        this.resetForm();
        this.showBudgetPlanForm = true;
        this.budgetPlanModal.open();
    }

    openEditModal(plan: Budget.BudgetPlan): void {
        this.isEditMode = true;
        this.editingBudgetPlan = plan;
        this.populateForm(plan);
        this.showBudgetPlanForm = true;
        this.budgetPlanModal.open();
    }

    private populateForm(plan: Budget.BudgetPlan): void {
        // Handle parsed value format for amounts
        const allocatedAmount =
            typeof plan.allocatedAmount === "number" ? plan.allocatedAmount : (plan.allocatedAmount?.parsedValue ?? 0);

        this.budgetPlanForm.patchValue({
            name: plan.name,
            budget: plan.budget,
            description: plan.description || "",
            planType: plan.planType,
            allocatedAmount: allocatedAmount,
            startDate: S25Util.date.toS25ISODateStr(plan.startDate),
            endDate: S25Util.date.toS25ISODateStr(plan.endDate),
            comments: plan.comments || "",
        });

        // Set minimum end date based on start date
        if (plan.startDate) {
            this.minEndDate = new Date(plan.startDate);
        }
    }

    getSubmitButtonText(): string {
        return this.isEditMode ? "Save Draft" : "Create Draft";
    }

    onStartDateChange(dateString: string): void {
        if (dateString) {
            this.minEndDate = new Date(dateString);
            this.cd.detectChanges();
        }
    }

    async onSubmit(): Promise<void> {
        if (this.budgetPlanForm.invalid) {
            this.markFormGroupTouched();
            return;
        }

        this.isSubmitting = true;
        this.cd.detectChanges();

        try {
            const formValue = this.budgetPlanForm.value;
            const planData: any = {
                name: formValue.name,
                description: formValue.description,
                planType: formValue.planType.itemId,
                allocatedAmount: formValue.allocatedAmount,
                startDate: S25Util.date.toS25ISODateTimeStr(formValue.startDate),
                endDate: S25Util.date.toS25ISODateTimeStr(formValue.endDate),
                // comments: formValue.comments, //no comments currently
                budgetId: formValue.budget.itemId,
                // Always create/update as draft - submission happens via separate button
                workflowStatus: null,
            };

            let result: Budget.BudgetPlanResponse;

            if (this.isEditMode && this.editingBudgetPlan) {
                const [updateResult, updateError] = await S25Util.Maybe(
                    BudgetPlanService.updateBudgetPlan(this.editingBudgetPlan.budgetPlanId, planData),
                );
                if (updateError) {
                    throw new Error(`Failed to update budget plan: ${S25Util.errorText(updateError)}`);
                }
                result = updateResult;
            } else {
                const [createResult, createError] = await S25Util.Maybe(BudgetPlanService.createBudgetPlan(planData));
                if (createError) {
                    throw new Error(`Failed to create budget plan: ${S25Util.errorText(createError)}`);
                }
                result = createResult;
            }

            this.planSaved.emit(result.budgetPlan);

            // Close modal
            this.showBudgetPlanForm = false;
            this.budgetPlanModal.close();
        } catch (error) {
            S25Util.showError(error, `Failed to ${this.isEditMode ? "update" : "create"} budget plan`);
        } finally {
            this.isSubmitting = false;
            this.cd.detectChanges();
        }
    }

    cancelForm = () => {
        this.showBudgetPlanForm = false;
        this.budgetPlanModal.close();
        this.resetForm();
        this.planCancelled.emit();
    };

    onBudgetSelect(item: Budget.BudgetDropdownItem): void {
        if (item && item.startDate && item.endDate) {
            const startDateStr = S25Util.date.toS25ISODateStr(item.startDate);
            const endDateStr = S25Util.date.toS25ISODateStr(item.endDate);

            // Set the start/end date from the parent budget unless they're already set by the user
            if (startDateStr && !this.budgetPlanForm.get("startDate")?.value) {
                this.budgetPlanForm.get("startDate")?.setValue(startDateStr);
                this.minEndDate = new Date(startDateStr);
            }
            if (endDateStr && !this.budgetPlanForm.get("endDate")?.value) {
                this.budgetPlanForm.get("endDate")?.setValue(endDateStr);
            }

            // Trigger change detection
            this.cd.detectChanges();
        }
    }

    private resetForm(): void {
        this.budgetPlanForm.reset();
        this.isEditMode = false;
        this.editingBudgetPlan = null;
        this.minEndDate = null;
    }

    private markFormGroupTouched(): void {
        Object.keys(this.budgetPlanForm.controls).forEach((key) => {
            const control = this.budgetPlanForm.get(key);
            control?.markAsTouched();
        });
    }
}
